import React from "react";
import { motion } from "framer-motion";
import { Link, Outlet, useLocation } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Logo } from "./ui/Logo";
import { ThemeToggle } from "./ui/ThemeToggle";
import { LanguageSwitcher } from "./ui/LanguageSwitcher";
import { spring } from "../utils/motion";
import { BoxIcon } from "lucide-react";
export interface NavItem {
  to: string;
  label: string;
  icon: BoxIcon;
  badge?: number;
}
export function AppShell({
  items
}: {
  items: NavItem[];
}) {
  const {
    pathname
  } = useLocation();
  const isActive = (to: string) => pathname.startsWith(to);
  return <div className="flex min-h-[100dvh] w-full bg-bg text-ink" data-id="element-50" data-mp="16j0wqko9goyg">
      <aside className="sticky top-0 hidden h-[100dvh] w-[96px] shrink-0 flex-col items-center border-r border-line py-5 lg:flex" data-id="element-51" data-mp="1bhvgbou1yhjm">
        <Link to="/" aria-label="Encorza home" className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-52" data-mp="1z6ohbzpj7fl3">
          <Logo showWord={false} data-id="element-53" data-mp="1rclrmm604y8t" />
        </Link>
        <nav aria-label="Main" className="mt-10 flex flex-col gap-2" data-id="element-54" data-mp="1rddb4ymbr5iq">
          {items.map(item => {
          const active = isActive(item.to);
          const Icon = item.icon;
          return <Link key={item.to} to={item.to} aria-current={active ? 'page' : undefined} className="group relative flex w-[76px] flex-col items-center gap-1 rounded-md py-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-55" data-mp="213q2b9on9wxu">
                {active && <motion.span layoutId="rail-pill" transition={spring.snappy} className="absolute inset-0 rounded-md bg-tint" data-id="element-56" data-mp="254ieobstchyc" />}
                <span className="relative" data-id="element-57" data-mp="2k0hkffsga60b">
                  <Icon className={twMerge('h-[22px] w-[22px] transition-colors duration-150', active ? 'text-brand-strong' : 'text-ink-3 group-hover:text-ink')} strokeWidth={active ? 2.4 : 2} data-id="element-58" data-mp="2jzg692g513ed" />
                  <NavBadge value={item.badge} data-id="element-59" data-mp="ppm2hmvdlekv" />
                </span>
                <span className={twMerge('relative text-xs font-bold transition-colors duration-150', active ? 'text-ink' : 'text-ink-3 group-hover:text-ink')} data-id="element-60" data-mp="kpq6a9sx6rfv">
                  {item.label}
                </span>
              </Link>;
        })}
        </nav>
        <div className="mt-auto flex flex-col items-center gap-3" data-id="element-61" data-mp="1jdid30a2px8z">
          <LanguageSwitcher id="rail" data-id="element-62" data-mp="1jfbmuofzgxl7" />
          <ThemeToggle data-id="element-63" data-mp="1ocv1vqki9ee7" />
        </div>
      </aside>

      <main className="min-w-0 flex-1 pb-[76px] lg:pb-0" data-id="element-64" data-mp="1odmy93lxfg5j">
        <Outlet data-id="element-65" data-mp="1tc7xazpfztse" />
      </main>

      <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-bg/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden" data-id="element-66" data-mp="1tawscwk9gfzp">
        <div className="mx-auto grid h-[76px] max-w-md grid-cols-4" data-id="element-67" data-mp="223gkde61rmbk">
          {items.map(item => {
          const active = isActive(item.to);
          const Icon = item.icon;
          return <Link key={item.to} to={item.to} aria-current={active ? 'page' : undefined} className="flex flex-col items-center justify-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand" data-id="element-68" data-mp="2c2fwx7txo23u">
                <span className="relative grid h-8 w-14 place-items-center" data-id="element-69" data-mp="5eyrw6l7opld">
                  {active && <motion.span layoutId="tab-pill" transition={spring.snappy} className="absolute inset-0 rounded-full bg-tint" data-id="element-70" data-mp="czmys9tpkmb" />}
                  <Icon className={twMerge('relative h-[22px] w-[22px]', active ? 'text-brand-strong' : 'text-ink-3')} strokeWidth={active ? 2.4 : 2} data-id="element-71" data-mp="3rmowmee3bqtj" />
                  <NavBadge value={item.badge} className="right-2" data-id="element-72" data-mp="o33qkzn0hkdb" />
                </span>
                <span className={twMerge('text-xs font-bold', active ? 'text-ink' : 'text-ink-3')} data-id="element-73" data-mp="j4sj2vk6vly9">{item.label}</span>
              </Link>;
        })}
        </div>
      </nav>
    </div>;
}
function NavBadge({
  value,
  className
}: {
  value?: number;
  className?: string;
}) {
  if (!value) return null;
  return <motion.span key={value} initial={{
    scale: 0.6
  }} animate={{
    scale: 1
  }} transition={spring.bouncy} className={twMerge('tabular absolute -right-2 -top-1.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-brand px-1 text-[11px] font-bold text-on-brand ring-2 ring-bg', className)} data-id="element-74" data-mp="16voe01jxyx9q">
      {value}
    </motion.span>;
}